import React from 'react'
import { system as s } from '@moonpig/launchpad-system'
import { breakpointDown, breakpointUp, styled } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { Combobox } from '@ariakit/react'
import { SEARCH_FORM_Z_INDEX } from './constants'

const getStyledInputClassNames = ({
  value,
  isSearchOpen,
}: {
  value?: string | number | readonly string[]
  isSearchOpen?: boolean
}) => ({
  className: `${value ? 'is-active' : ''} ${isSearchOpen ? 'is-open' : ''}`,
})

const StyledCombobox = styled(Combobox)<{
  isSearchOpen?: boolean
}>`
  ${s({
    py: { xs: 2, md: 4 },
    typography: 'typeBodyLabel',
    bgcolor: 'colorBackground03',
    color: 'colorTextLabel',
    pl: 6,
    m: 0,
    borderColor: 'colorPrimary02',
    border: 2,
    borderRadius: 6,
    fontWeight: 'bold',
  })}
  display: block;
  width: 100%;
  position: absolute;
  z-index: ${SEARCH_FORM_Z_INDEX};

  ${({ theme, isSearchOpen }) =>
    s({
      minHeight: !isSearchOpen
        ? { xs: '40px', md: spacingPx(12)({ theme }) }
        : spacingPx(12)({ theme }),
    })};

  &::-ms-clear {
    display: none;
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-appearance: none;
  }

  &::placeholder {
    ${s({ color: 'colorTextLabel' })}
  }

  &:focus {
    ${s({
      borderColor: 'colorPrimary02',
      border: 3,
      color: 'colorTextLabel',
    })}
    outline: transparent;
  }

  &.is-active {
    ${s({
      pr: 15,
      bgcolor: 'colorBackground03',
      color: 'colorTextLabel',
      border: 3,
      borderColor: 'colorPrimary02',
    })}
  }

  &.is-open {
    border: none;
    border-bottom: 1px solid;
    border-radius: 0;
    ${s({
      borderColor: 'colorBorder03',
      color: 'colorTextLabel',
      pl: 6,
      right: 0,
      bgcolor: 'colorBackground01',
      fontWeight: 'normal',
    })}
    ${breakpointDown('md')} {
      width: calc(100% - 48px);
    }
    ${breakpointUp('md')} {
      border-radius: 8px 8px 0px 0px;
    }
  }

  &:focus ~ button:nth-of-type(2) {
    ${breakpointUp('md')} {
      border-radius: 8px;
    }
  }

  &.is-active ~ button:nth-of-type(3) {
    border-radius: 24px;
  }

  &.is-open ~ button:nth-of-type(3) {
    ${breakpointUp('md')} {
      border-radius: 0 8px 0 0;
    }

    ${breakpointDown('md')} {
      border-radius: 0;
    }
  }

  &:focus ~ button:nth-of-type(3) {
    ${s({
      right: 0,
      color: 'colorBlack00',
      bgcolor: 'colorPrimary02',
    })}

    ${breakpointUp('md')} {
      border-radius: 24px;
    }
  }

  &.is-open:focus ~ button:nth-of-type(3) {
    ${breakpointUp('md')} {
      border-radius: 0px 8px 0px 0px;
    }

    ${breakpointDown('md')} {
      border-radius: 0;
    }
  }
`

type SearchInputProps = {
  isSearchOpen?: boolean
  value?: string | number | string[]
  placeholder: string
} & React.HTMLProps<HTMLInputElement>

export const Input = React.forwardRef<
  HTMLInputElement | null,
  SearchInputProps
>((props, ref) => {
  const { isSearchOpen, value, placeholder, as, ...filteredInputProps } = props

  return (
    <StyledCombobox
      {...filteredInputProps}
      placeholder={placeholder}
      ref={ref}
      as={
        as as
          | keyof React.JSX.IntrinsicElements
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          | React.ComponentType<any>
      }
      isSearchOpen={isSearchOpen}
      className={
        getStyledInputClassNames({
          value,
          isSearchOpen,
        }).className
      }
    />
  )
})

Input.displayName = 'SearchInput'
